import { Box, Dialog, Skeleton } from '@mui/material';
import { useState } from 'react';
import { KitButton, KitList } from '../common/MobileKit';
import { MobileActionItem, MobileListRow } from '../common/MobileCrud';
import MobileSheet from '../common/MobileSheet';
import { protoIcon } from '../common/ProtoIcon';
import { KitEmpty, KitField, KitFab, KitInput, KitSearch, KitSeg, KitTextarea } from '../common/MobileKitExtras';
import { appBarButtonSx } from '../layout/AppBarActions';

/**
 * Montadoras no shell mobile — `prototipos-mobile/veiculos/montadoras.html`.
 * Só apresentação: dados, CRUD e mensagens vêm de `pages/vehicles/ManufacturersModal.js`.
 * Renderizada como tela cheia (o protótipo é uma página com barra superior própria).
 */

const BackIcon = protoIcon('back');
const CarIcon = protoIcon('car');
const PlusIcon = protoIcon('plus');
const EditIcon = protoIcon('edit');
const TrashIcon = protoIcon('trash');

const TYPE_OPTIONS = [
    { value: 'car', label: 'Carro' },
    { value: 'motorcycle', label: 'Moto' },
    { value: 'bicycle', label: 'Bicicleta' },
];
const TYPE_LABELS = { car: 'Carro', motorcycle: 'Moto', bicycle: 'Bicicleta' };

export default function MobileManufacturers({
    open,
    onClose,
    manufacturers,
    loading,
    onNew,
    onEdit,
    onDelete,
    form,
}) {
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState(null);

    const term = search.trim().toLowerCase();
    const items = term
        ? manufacturers.filter((m) => `${m.name || ''} ${m.description || ''}`.toLowerCase().includes(term))
        : manufacturers;

    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            slotProps={{ paper: { sx: { backgroundColor: 'background.default', backgroundImage: 'none' } } }}
        >
            <Box
                component="header"
                sx={{
                    flexShrink: 0,
                    position: 'sticky',
                    top: 0,
                    zIndex: 4,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    p: 'calc(10px + env(safe-area-inset-top, 0px)) 12px 10px',
                    minHeight: 56,
                    boxSizing: 'border-box',
                    backgroundColor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Box component="button" type="button" onClick={onClose} aria-label="Voltar" sx={appBarButtonSx}>
                    <BackIcon size={20} />
                </Box>
                <Box component="h1" sx={{ m: 0, fontSize: 17, fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.4 }}>Montadoras</Box>
            </Box>

            <Box sx={{ p: '16px 16px calc(88px + env(safe-area-inset-bottom, 0px))', fontSize: 14, lineHeight: 1.4 }}>
                <KitSearch
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar montadora…"
                    inputProps={{ 'aria-label': 'Buscar montadora', inputMode: 'search', enterKeyHint: 'search' }}
                />
                <Box sx={{ mt: '12px' }} />

                {loading && manufacturers.length === 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {[1, 2, 3, 4].map((n) => <Skeleton key={n} variant="rounded" height={60} sx={{ borderRadius: '16px' }} />)}
                    </Box>
                )}
                {!loading && items.length === 0 && (
                    <KitEmpty icon={CarIcon} title="Nenhuma montadora encontrada">
                        Ajuste a busca ou cadastre uma nova montadora.
                    </KitEmpty>
                )}
                {items.length > 0 && (
                    <KitList>
                        {items.map((item) => (
                            <MobileListRow
                                key={item.id}
                                icon={CarIcon}
                                title={item.name}
                                sub={[TYPE_LABELS[item.type_vehicle] || item.type_vehicle, item.description].filter(Boolean).join(' · ')}
                                chevron
                                onClick={() => setSelected(item)}
                            />
                        ))}
                    </KitList>
                )}
            </Box>

            <KitFab label="Nova" icon={PlusIcon} onClick={onNew} />

            <MobileSheet open={Boolean(selected)} onClose={() => setSelected(null)} title={selected?.name || 'Montadora'}>
                {selected && selected.editable === false ? (
                    <Box sx={{ fontSize: 13, color: 'text.disabled', p: '4px 4px 12px', lineHeight: 1.4 }}>
                        Esta montadora é padrão do sistema e não pode ser editada ou excluída.
                    </Box>
                ) : (
                    <>
                        <MobileActionItem icon={EditIcon} label="Editar" onClick={() => { const t = selected; setSelected(null); onEdit(t); }} />
                        <MobileActionItem icon={TrashIcon} label="Excluir" destructive chevron={false} onClick={() => { const t = selected; setSelected(null); onDelete(t); }} />
                    </>
                )}
            </MobileSheet>

            <MobileSheet
                open={form.open}
                onClose={form.onClose}
                title={form.editing ? 'Editar montadora' : 'Nova montadora'}
                footer={(
                    <>
                        <KitButton sx={{ flex: 1 }} onClick={form.onClose}>Cancelar</KitButton>
                        <KitButton variant="primary" sx={{ flex: 1 }} onClick={form.onSubmit} disabled={form.saving}>
                            {form.saving ? 'Salvando...' : 'Salvar'}
                        </KitButton>
                    </>
                )}
            >
                <KitField label="Tipo de veículo" required>
                    <KitSeg options={TYPE_OPTIONS} value={form.data.type_vehicle} onChange={(value) => form.onChange({ ...form.data, type_vehicle: value })} />
                </KitField>
                <KitField label="Nome" required help={form.errors.name ? 'Campo obrigatório' : undefined} error={form.errors.name}>
                    <KitInput type="text" value={form.data.name} onChange={(e) => form.onChange({ ...form.data, name: e.target.value }, 'name')} error={form.errors.name} disabled={form.saving} />
                </KitField>
                <KitField label="Descrição" required help={form.errors.description ? 'Campo obrigatório' : undefined} error={form.errors.description}>
                    <KitTextarea value={form.data.description} onChange={(e) => form.onChange({ ...form.data, description: e.target.value }, 'description')} sx={form.errors.description ? { borderColor: 'error.main' } : undefined} disabled={form.saving} />
                </KitField>
            </MobileSheet>
        </Dialog>
    );
}
